import React, { useEffect, useState } from "react"
import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import { createUseStyles, useTheme } from "react-jss"
import { v4 as uuidv4 } from "uuid"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import {
  rawDataQualityInfoAsync,
  resetRawDataTable,
  rowSlection,
  toggleRawDataDetails,
} from "./DataMetaDataSlice"
import { ABBREVIATE_NUMBER } from "../../util/CommonMethods"
const profilingValKeys = ["Most Freq. Count", "Mean", "Null"]
const useStyles = createUseStyles({
  tableContainer: {
    padding: "8px",
    borderRadius: "5px",
    background: "#FFF",
    boxShadow: "0px 4px 10px 0px rgba(0, 0, 0, 0.10)",
    width: "auto !important",
  },
  table: {},
  tableHead: {
    backgroundColor: "#f5f5f5",
  },
  headerColor: {
    color: "#7D7D7D",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontSize: "11px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "14px",
    textAlign: "center",
  },
  tableCell: {
    padding: "8px",
    fontSize: "14px",
    borderRight: "1px solid #e0e0e0",
    "&:first-child": {
      borderTopLeftRadius: "4px",
      textAlign: "left",
    },
    "&:last-child": {
      borderTopRightRadius: "4px",
      borderRight: "none",
    },
  },
  rowDataNameCell: {
    color: "#7D7D7D !important",
    fontFeatureSettings: "'clig' off, 'liga' off !important",
    fontSize: "16px !important",
    fontStyle: "normal !important",
    fontWeight: 700,
    lineHeight: "14px !important",
  },
  schemeCheckCell: {
    display: "flex",
    width: "106px",
    padding: "3px 12px",
    justifyContent: "center",
    alignItems: "center",
    gap: "2px",
    borderRadius: "5px",
    background: "#DAE3FB",
    color: "#0A57D5",
    marginLeft: "auto",
    marginRight: "auto",
  },
  bars: {
    display: "flex",
    gap: 5,
  },
  pass: {
    width: 80,
    background: "#27AF8D",
    height: 4,
  },
  fail: {
    width: 80,
    background: "#EAEAEA",
    height: 4,
  },
  label: {
    color: "#171717",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    letterSpacing: "0.28px",
    textAlign: "center",
  },
  barWrapper: {
    width: "fit-content",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  processedRowsPercentage: {
    display: "inline-flex",
    padding: "1px 6px",
    justifyContent: "center",
    alignItems: "center",
    gap: "7px",
    background: "#CFFFE1",
  },
  processedRowsPercentageText: {
    color: "#30D468",
    fontSize: "10px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    letterSpacing: "0.2px",
  },
  processedRowsWrapper: {
    display: "flex",
    gap: "10px",
  },
  tickMark: {
    width: "22px",
    height: "20px",
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    background: "#CFFFE1",
  },
  tickMarkRed: {
    width: "22px",
    height: "20px",
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
    background: "#F6CBC7",
  },
  schemaPassWrapper: {
    display: "flex",
    gap: 3,
    marginRight: 10,
  },
  enforcedCount: {
    color: "#5C5C5C",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "normal",
    letterSpacing: "0.24px",
    marginRight: 5,
  },
  passWrapper: {
    color: "#1F1F1F",
    fontFamily: "Montserrat",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "normal",
    letterSpacing: "0.24px",
    border: "0.2px solid black",
    width: "43px",
    textAlign: "center",
  },
  schemaCount: {
    color: "#1F1F1F",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    letterSpacing: "0.32px",
  },
  schemaWrapper: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  right: {
    color: "#30D468",
    display: "flex",
    gap: 5,
    padding: "2px 6px",
    alignItems: "center",
  },
  wrong: {
    color: "#F43F32",
    display: "flex",
    gap: 5,
    padding: "2px 6px",
    alignItems: "center",
  },
  qualityObj: {
    display: "flex",
    gap: 5,
  },
  qualityWrapper: {
    display: "flex",
    gap: 10,
    justifyContent: "space-around",
  },
  profiling: {
    display: "flex",
    flexDirection: "column",
    gap: "7px",
    textAlign: "left",
  },
  blackText: {
    color: "black",
  },
})

const RawDataDetailsTable = () => {
  const theme: any = useTheme()
  const classes = useStyles({ theme })
  const data = useAppSelector(
    (store) => store.dataMetaData.rawDataInfoTableData,
  )
  const [rows, setRows] = useState([])

  // mapping and formating for profiling,schema,null_status and unique_status
  useEffect(() => {
    try {
      const finalRows = data.map((obj: any) => {
        return {
          name: obj.column,
          dataType: obj.data_type,
          profiling: `${ABBREVIATE_NUMBER(
            obj["% most freuent count"],
            2,
          )}% **** ${ABBREVIATE_NUMBER(
            obj["mean"],
            2,
          )} **** ${ABBREVIATE_NUMBER(obj["null %"], 2)}%`,
          schema: {
            isPass: obj.schema,
          },
          null_status: obj.null_status,
          unique_status: obj.unique_status,
        }
      })
      setRows(finalRows)
    } catch (error) {
      console.error(error)
    }
  }, [data])

  const handleClick = () => {}
  const dispatch = useAppDispatch()
  const selectedRawData = useAppSelector(
    (state) => state.dataMetaData.selectedRawData,
  )
  // default effects to run
  useEffect(() => {
    dispatch(resetRawDataTable())
    dispatch(rawDataQualityInfoAsync())
  }, [])
  // delete handle for chip deselection
  const handleDelete = () => {
    dispatch(
      toggleRawDataDetails({
        selectedRawData: "",
        isRdqDetailsTableActive: false,
      }),
    )
    dispatch(rowSlection(""))
  }
  return (
    <div>
      <TableContainer className={classes.tableContainer}>
        <Box
          sx={{
            padding: "8px",
            border: "2px dashed rgb(142, 142, 142)",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <Typography variant="subtitle2">Filters :</Typography>
          <Chip
            label={selectedRawData}
            onClick={handleClick}
            onDelete={handleDelete}
            variant="outlined"
          />
        </Box>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableCell}>
                <Typography variant="tableHeader" align="left">
                  {" "}
                  Column Name
                </Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography variant="tableHeader">Data Type</Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography variant="tableHeader"> Profiling</Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography variant="tableHeader"> Schema</Typography>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <Typography
                  sx={{ display: "flex", flexDirection: "column", gap: "7px" }}
                  variant="tableHeader"
                >
                  <Box>Quality Check</Box>
                  <Box sx={{ display: "flex", justifyContent: "space-around" }}>
                    <Typography variant="tableHeader">Not Null</Typography>
                    <Typography variant="tableHeader">Unique</Typography>
                  </Box>
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row: any) => {
              const nullStatus = getStatusClass(row.null_status, classes)
              const uniqueStatus = getStatusClass(row.unique_status, classes)
              return (
                <TableRow key={uuidv4()}>
                  <TableCell
                    className={
                      classes.tableCell + " " + classes.rowDataNameCell
                    }
                  >
                    {row.name}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.schemeCheckCell}>
                      {row.dataType}
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 10,
                        justifyContent: "center",
                      }}
                    >
                      <div className={classes.profiling}>
                        {row.profiling.split("****").map((val: any, i: any) => {
                          return (
                            <div key={uuidv4()}>{profilingValKeys[i]}:</div>
                          )
                        })}
                      </div>
                      <div className={classes.profiling}>
                        {row.profiling.split("****").map((val: any) => {
                          return <div key={uuidv4()}>{val}</div>
                        })}
                      </div>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.schemaWrapper}>
                      {row.schema.isPass ? (
                        <div className={classes.schemaPassWrapper}>
                          <div className={classes.tickMark}>
                            <svg
                              width="12"
                              height="9"
                              viewBox="0 0 12 9"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M1 4.68421L4.15789 7.8421L11 1"
                                stroke="#30D468"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                          <div className={classes.passWrapper}> Pass</div>
                        </div>
                      ) : (
                        ""
                      )}
                    </div>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <div className={classes.qualityWrapper}>
                      <div className={nullStatus}>
                        <StatusIndicator
                          status={row.null_status}
                          classes={classes}
                        />
                      </div>
                      <div className={uniqueStatus}>
                        <StatusIndicator
                          status={row.unique_status}
                          classes={classes}
                        />
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  )
}

export default RawDataDetailsTable

const getStatusClass = (status: string, classes: any) => {
  switch (status) {
    case "PASS":
      return classes.right
    case "Not-Applicable":
      return classes.blackText
    default:
      return classes.wrong
  }
}

const StatusIndicator = ({ status, classes }: any) => {
  const renderContent = () => {
    switch (status.toLowerCase()) {
      case "pass":
        return (
          <div className={classes.schemaPassWrapper}>
            <div className={classes.tickMark}>
              <svg
                width="12"
                height="9"
                viewBox="0 0 12 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 4.68421L4.15789 7.8421L11 1"
                  stroke="#30D468"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className={classes.passWrapper}>Pass</div>
          </div>
        )
      case "fail":
        return (
          <div className={classes.schemaPassWrapper}>
            <div className={classes.tickMarkRed}>
              <svg
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M8.43472 9.21854L1.78341 1.00015" stroke="#F43F32" />
                <path d="M9.61014 2.00791L1.39175 8.65921" stroke="#F43F32" />
              </svg>
            </div>
            <div className={classes.passWrapper}>Fail</div>
          </div>
        )
      case "not-applicable":
        return (
          <div className={classes.notApplicableWrapper}>Not Applicable</div>
        )
      default:
        return null
    }
  }

  return <>{renderContent()}</>
}
